import type { DegreeSeekingProgram } from "~/server/resolvers/resolvers-types";
import type {
  TuitionCalculatorCollapseItem,
  TuitionCalculatorDegreeType,
  TuitionCalculatorResultSnapshot,
  TuitionCalculatorSavingsValidation,
  TuitionCalculatorSelections,
  TuitionCalculatorValidationErrors
} from "~/types/tuition-calculator";
import type {
  SemesterTotalsByYear,
  TuitionEstimateInput,
  TuitionSemesterItem
} from "~/types/types";

const SUPPORTED_CATEGORIES: readonly TuitionCalculatorDegreeType[] = [
  "Undergraduate",
  "Exploratory",
  "Graduate",
  "Certificates"
];

const DEFAULT_TOTAL_CREDITS: Record<TuitionCalculatorDegreeType, number> = {
  Undergraduate: 120,
  Exploratory: 120,
  Graduate: 30,
  Certificates: 15
};

export const CREDITS_RANGE_MESSAGE = "Enter credits between 1 to 18 to continue.";

const RESIDENCY_CAP_CREDIT_THRESHOLD = 11;
const TRANSFER_CREDIT_LIMIT = 90;
const MAX_FINANCIAL_AID_AMOUNT = 99_999;
const DEFAULT_TRANSFER_CREDIT_MINIMUM = 0;

const USD_CURRENCY_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2
});

export const createInitialSelections = (): TuitionCalculatorSelections => ({
  degreeType: null,
  program: null,
  creditOption: null,
  creditsPerSemester: 0,
  residencyStatus: null,
  transferCredits: 0,
  grants: 0,
  scholarships: 0,
  employerTuitionBenefit: 0,
  includeSummer: true
});

export const cloneSelections = (
  selections: TuitionCalculatorSelections
): TuitionCalculatorSelections => ({ ...selections });

const isWholeNumberAtLeastZero = (value: number | null): value is number =>
  value !== null && Number.isFinite(value) && Number.isInteger(value) && value >= 0;

const isCurrencyAmount = (value: number | null): value is number =>
  value !== null && Number.isFinite(value) && value >= 0 && Number(value.toFixed(2)) === value;

export const filterPrograms = (
  programs: readonly DegreeSeekingProgram[],
  degreeType: TuitionCalculatorDegreeType
): DegreeSeekingProgram[] => {
  if (!SUPPORTED_CATEGORIES.includes(degreeType)) return [];

  return programs
    .filter((program) => program.category?.title === degreeType)
    .sort((first, second) => (first.title ?? "").localeCompare(second.title ?? ""));
};

export const getFullTimeCredits = (degreeType: TuitionCalculatorDegreeType): number =>
  degreeType === "Graduate" ? 9 : 12;

export const getTotalCredits = (
  program: DegreeSeekingProgram,
  degreeType: TuitionCalculatorDegreeType
): number => {
  const parsed = Number(program.totalCreditHours);
  if (Number.isFinite(parsed) && parsed > 0) return parsed;
  return DEFAULT_TOTAL_CREDITS[degreeType];
};

export const getTransferCreditLimit = (): number => TRANSFER_CREDIT_LIMIT;

export const getMaxFinancialAidAmount = (): number => MAX_FINANCIAL_AID_AMOUNT;

const parseTransferCreditBoundary = (value: string | number | null | undefined): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
};

const getTransferCreditRange = (
  program: DegreeSeekingProgram | null
): { minimum: number; maximum: number; configured: boolean } => {
  const configuredMinimum = parseTransferCreditBoundary(program?.transferCreditsMinValue);
  const configuredMaximum = parseTransferCreditBoundary(program?.transferCreditsMaxValue);
  const minimum = configuredMinimum ?? DEFAULT_TRANSFER_CREDIT_MINIMUM;
  const maximum = configuredMaximum ?? getTransferCreditLimit();

  return maximum >= minimum
    ? { minimum, maximum, configured: configuredMinimum !== null || configuredMaximum !== null }
    : {
        minimum: DEFAULT_TRANSFER_CREDIT_MINIMUM,
        maximum: getTransferCreditLimit(),
        configured: false
      };
};

const toPlainText = (value: string): string =>
  value
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const getTransferCreditRangeMessage = (
  program: DegreeSeekingProgram | null,
  minimum: number,
  maximum: number,
  configured: boolean
): string => {
  const cmsMessage = program?.transferCreditsError ? toPlainText(program.transferCreditsError) : "";
  if (cmsMessage) return cmsMessage;
  if (configured) {
    return `The min value of transfer credits for this program is ${minimum} and max value is ${maximum}.`;
  }
  return "Enter less than " + String(maximum + 1) + " transfer credits.";
};

export const shouldDisplayTransferCredits = (
  selections: Pick<TuitionCalculatorSelections, "degreeType" | "program">
): boolean => {
  if (selections.degreeType === "Undergraduate" || selections.degreeType === "Exploratory") {
    return selections.program?.displayTransferCredits !== false;
  }

  if (selections.degreeType === "Graduate") {
    return selections.program?.displayTransferCredits === true;
  }

  return false;
};

export const normalizeSelectionsForCategory = (
  selections: TuitionCalculatorSelections
): TuitionCalculatorSelections => {
  if (selections.degreeType === "Certificates") {
    return {
      ...selections,
      transferCredits: 0,
      grants: 0
    };
  }

  return {
    ...selections,
    transferCredits: shouldDisplayTransferCredits(selections) ? selections.transferCredits : 0,
    grants: selections.degreeType === "Graduate" ? 0 : selections.grants
  };
};

export const residencyAffectsCost = (selections: TuitionCalculatorSelections): boolean =>
  Boolean(
    selections.program &&
    !selections.program.costOverride &&
    Number.isInteger(selections.creditsPerSemester) &&
    selections.creditsPerSemester > RESIDENCY_CAP_CREDIT_THRESHOLD
  );

export const validateDetails = (
  selections: TuitionCalculatorSelections
): TuitionCalculatorValidationErrors => {
  const errors: TuitionCalculatorValidationErrors = {};
  if (!selections.degreeType) errors.degreeType = "Select a degree type.";
  if (!selections.program) errors.program = "Select a program.";
  if (selections.degreeType !== "Certificates" && !selections.creditOption) {
    errors.creditsPerSemester = "Select student type";
  } else if (
    selections.degreeType !== "Certificates" &&
    (!Number.isInteger(selections.creditsPerSemester) ||
      selections.creditsPerSemester < 1 ||
      selections.creditsPerSemester > 18)
  ) {
    errors.creditsPerSemester = CREDITS_RANGE_MESSAGE;
  }
  if (residencyAffectsCost(selections) && !selections.residencyStatus) {
    errors.residencyStatus = "Select your residency status.";
  }
  return errors;
};

export const validateSavings = (
  selections: TuitionCalculatorSelections,
  _exceptionCodes: readonly string[]
): TuitionCalculatorSavingsValidation => {
  const normalized = normalizeSelectionsForCategory(selections);
  const errors: TuitionCalculatorValidationErrors = {};
  const warnings: TuitionCalculatorValidationErrors = {};
  const { minimum, maximum, configured } = getTransferCreditRange(normalized.program);
  const transferCreditsHidden = !shouldDisplayTransferCredits(normalized);

  if (transferCreditsHidden) {
    // Hidden transfer credits are reset by the UI and ignored by validation.
  } else if (!isWholeNumberAtLeastZero(normalized.transferCredits)) {
    errors.transferCredits = "Enter transfer credits as a whole number of zero or greater.";
  } else if (normalized.transferCredits < minimum || normalized.transferCredits > maximum) {
    errors.transferCredits = getTransferCreditRangeMessage(
      normalized.program,
      minimum,
      maximum,
      configured
    );
    if (
      minimum === DEFAULT_TRANSFER_CREDIT_MINIMUM &&
      maximum === getTransferCreditLimit() &&
      normalized.transferCredits > maximum
    ) {
      warnings.transferCredits = "transfer-limit";
    }
  }

  const moneyFields = [
    ["grants", normalized.grants],
    ["scholarships", normalized.scholarships],
    ["employerTuitionBenefit", normalized.employerTuitionBenefit]
  ] as const;
  for (const [field, value] of moneyFields) {
    if (!isCurrencyAmount(value)) {
      errors[field] = "Enter an amount of zero or greater with no more than two decimal places.";
    } else if (value > MAX_FINANCIAL_AID_AMOUNT) {
      errors[field] = "Enter less than $100,000 to continue.";
    }
  }
  return { errors, warnings };
};

export const buildTuitionEstimateInput = (
  source: TuitionCalculatorSelections
): TuitionEstimateInput => {
  const selections = normalizeSelectionsForCategory(source);
  const program = selections.program;
  const residencyStatus =
    selections.residencyStatus ?? (residencyAffectsCost(selections) ? null : "res");
  if (
    !program ||
    program.costOverride ||
    !selections.degreeType ||
    !residencyStatus ||
    !Number.isInteger(selections.creditsPerSemester) ||
    selections.creditsPerSemester < 1 ||
    selections.creditsPerSemester > 18 ||
    !program.programCode ||
    !program.costAcademicYear ||
    !program.latestAcademicYear
  ) {
    throw new Error("Tuition request metadata is incomplete.");
  }
  const feeCode =
    residencyStatus === "res" ? program.resProgramFeeCode : program.noresProgramFeeCode;
  return {
    costAcademicYear: program.costAcademicYear,
    latestAcademicYear: program.latestAcademicYear,
    program_code: program.programCode,
    residency_status: residencyStatus,
    degree_type: selections.degreeType === "Undergraduate" ? "UGRD" : "GRAD",
    fee_code: feeCode ?? "",
    total_credits: getTotalCredits(program, selections.degreeType),
    transfer_credits: Math.min(selections.transferCredits ?? 0, getTransferCreditLimit()),
    grants: selections.grants ?? 0,
    scholarships: selections.scholarships ?? 0,
    employer_tuition_benefit: selections.employerTuitionBenefit ?? 0,
    credits_per_semester: selections.creditsPerSemester,
    include_summer: selections.includeSummer
  };
};

export const formatAcademicYearLabel = (academicYear: number | string): string => {
  const endingYear = Number(academicYear);
  return Number.isInteger(endingYear) ? String(endingYear - 1) + "–" + String(endingYear) : "";
};

export const formatTermLabel = (
  term: "fall" | "spring" | "summer",
  academicYear: number | string
): string => {
  const endingYear = Number(academicYear);
  const label = term.charAt(0).toUpperCase() + term.slice(1);
  if (!Number.isInteger(endingYear)) return label;
  const calendarYear = term === "fall" ? endingYear - 1 : endingYear;
  return `${label} ${calendarYear}`;
};

export const formatCurrency = (value: unknown): string | null => {
  if (typeof value !== "number" && typeof value !== "string") return null;

  const normalized = typeof value === "string" ? value.trim().replace(/,/g, "") : value;
  if (normalized === "") return null;

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? USD_CURRENCY_FORMATTER.format(parsed) : null;
};

export const toCollapseItems = (
  items: readonly TuitionSemesterItem[]
): TuitionCalculatorCollapseItem[] =>
  items.map((item) => ({
    title: item.descr,
    amount: formatCurrency(item.value) ?? ""
  }));

export const cloneResultSnapshot = (
  snapshot: TuitionCalculatorResultSnapshot
): TuitionCalculatorResultSnapshot => {
  const estimates = snapshot.estimates.map((estimate) => {
    const { summer, ...estimateWithoutSummer } = estimate;

    return {
      ...estimateWithoutSummer,
      fall: estimate.fall.map((item) => ({ ...item })),
      spring: estimate.spring.map((item) => ({ ...item })),
      ...(summer ? { summer: summer.map((item) => ({ ...item })) } : {})
    };
  });
  const semesterTotals: SemesterTotalsByYear = {};

  for (const [academicYear, totals] of Object.entries(snapshot.semesterTotals)) {
    semesterTotals[academicYear] = { ...totals };
  }

  return { estimates, semesterTotals };
};
